import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import { SearchInput } from "@src/components/SearchInput";
import PageTitle from "@src/components/PageTitle";
import CompactIcon from "@src/assets/img/compacts-icon.svg";
import CompactIconActive from "@src/assets/img/compacts-icon-active.svg";
import LibraryList from "@src/components/LibraryList";
import ErrorLabel from "@src/components/ErrorLabel";
import Spinner from "@src/components/Spinner";
import { useNotification } from "@src/context/NotificationContext";
import { useSettings } from "@src/context/SettingsContext";
import { useFetchTracksQuery } from "@src/queries/trackQueries";
import FilterIcon from "@src/assets/img/filter_icon.svg";
import FilterIconActive from "@src/assets/img/filter-icon-active.svg";
import {
  PlaylistOverviewHeader,
  PlaylistOverviewHeaderCenter,
  PlaylistOverviewHeaderLeft,
  PlaylistOverviewHeaderRight,
  ButtonSection,
  PlaylistOverviewHeaderRightContainer,
  BottomButtonsContainer,
  TotalLabelWrapper,
  TotalLabel,
  SearchInputWrapper,
} from "./Tracks.styles";
import { CompactActionButton } from "@src/components/CompactActionButton";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { ITrack } from "@src/types/track";
import RenderContentStatus from "@src/components/RenderContentStatus";
import { useSelector } from "react-redux";
import { RootState } from "@src/redux/store";
import { useAuthState } from "@src/hooks/useAuth";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import {
  setDeleteTrackFromLibraryState,
  setLibraryFilters,
} from "@src/redux/slices/librarySlice";
import ContentDrawer from "@src/components/ContentDrawer";
import { CircleActionButton } from "@src/components/CircleButton";
import { theme } from "@src/theme";
import FilterTracksForm from "./FilterTracksForm";
import AudioWaveform, {
  AudioWaveformRef,
} from "@src/components/MusicPlayer/AudioWaveform";
import {
  setCurrentPlayingTrack,
  setIsPlaying,
} from "@src/redux/slices/trackPlayerSlice";
import { FaBackward, FaForward } from "react-icons/fa";
import TrackInformation from "@src/components/TrackInformation";
import { useFormattingSeconds } from "@src/hooks/useFormattingSeconds";
import debounce from "lodash.debounce";

const schema = yup.object().shape({
  searchText: yup
    .string()
    .max(50, "Search text must be less than 50 characters"),
});
const Tracks = (): React.JSX.Element => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const { addNotification } = useNotification();
  const { toggleLibraryTable, tableSettings } = useSettings();
  const { status } = useAppSelector(
    (state) => state.libraryPage.deleteTrackFromLibrary
  );
  const dispatch = useAppDispatch();
  const curator = useSelector((state: RootState) => state.auth.curator);
  const { role: roleData } = useAuthState();
  const { filters } = useAppSelector((state: RootState) => state.libraryPage);
  const {
    isLoading: fetchingTracks,
    data: tracksList,
    refetch: reFetchTracks,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchTracksQuery(filters, curator?.id, roleData?.type);
  const {
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      searchText: "",
    },
  });
  const {
    currentPlayingTrack: trackSelected,
    trackURL,
    isPlaying,
  } = useAppSelector((state) => state.trackPlayer);
  useEffect(() => {
    if (status === "succeeded" && !fetchingTracks) {
      addNotification("Track deleted successfully", "success");
      dispatch(
        setDeleteTrackFromLibraryState({
          status: "idle",
          loading: false,
          error: null,
        })
      );
      reFetchTracks();
    }
  }, [status]);

  const debouncedDispatch = useMemo(
    () =>
      debounce((value: string) => {
        dispatch(
          setLibraryFilters({
            searchText: value,
          })
        );
      }, 500),
    [dispatch]
  );

  const tableEndRef = useRef<HTMLTableRowElement | null>(null);
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );
  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    });

    if (tableEndRef.current) observer.observe(tableEndRef.current);

    return () => {
      if (tableEndRef.current) observer.unobserve(tableEndRef.current);
    };
  }, [handleObserver]);
  const waveformRef = useRef<AudioWaveformRef>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);

  const handlePlayButtonClick = (row: ITrack): void => {
    if (row.documentId === trackSelected?.documentId) {
      if (isPlaying) {
        dispatch(setIsPlaying(false));
        return waveformRef.current?.pauseAudio();
      } else {
        dispatch(setIsPlaying(true));
        return waveformRef.current?.playAudio();
      }
    }
    dispatch(setCurrentPlayingTrack(row));
    dispatch(setIsPlaying(true));
  };
  const handleOnSelectSingleRow = (row: ITrack): void => {
    dispatch(setCurrentPlayingTrack(row));
  };
  useEffect(() => {
    const interval = setInterval(() => {
      if (waveformRef.current) {
        setCurrentTime(waveformRef.current.getCurrentTime());
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    return () => {
      dispatch(setIsPlaying(false));
      dispatch(setCurrentPlayingTrack(null));
    };
  }, []);

  const BackwardIcon = FaBackward as unknown as React.ComponentType<any>;
  const ForwardIcon = FaForward as unknown as React.ComponentType<any>;

  const totalCount = useMemo(
    () =>
      (tracksList?.pages?.[0] as AxiosResponse<ApiResponse<ITrack[]>>)?.data
        ?.meta?.pagination?.total ?? "0",
    [tracksList]
  );

  const tracks = useMemo(
    () =>
      (
        (tracksList?.pages as AxiosResponse<ApiResponse<ITrack[]>>[]) ?? []
      ).flatMap((response) => response.data.data) || [],
    [tracksList]
  );
  return (
    <WrapperContainer noScroll>
      <PageTopSection>
        <PlaylistOverviewHeader>
          <PlaylistOverviewHeaderLeft>
            <PageTitle>Library</PageTitle>
            <div
              style={{
                display: "flex",
                gap: theme.margins.medium,
              }}
            >
              <CompactActionButton
                $tooltipContent="Compact view"
                $active={tableSettings?.libraryTable}
                onClick={toggleLibraryTable}
              >
                <img
                  src={
                    tableSettings?.libraryTable
                      ? CompactIconActive
                      : CompactIcon
                  }
                  alt="compact"
                  style={{ width: "50%", objectFit: "contain" }}
                />
              </CompactActionButton>
              <CircleActionButton
                $tooltipContent="Filter"
                $active={isFilterDrawerOpen}
                onClick={() => {
                  setIsFilterDrawerOpen((prev) => !prev);
                }}
              >
                <img
                  src={isFilterDrawerOpen ? FilterIconActive : FilterIcon}
                  alt="filter"
                  style={{ width: "70%", objectFit: "contain" }}
                />
              </CircleActionButton>
            </div>
          </PlaylistOverviewHeaderLeft>
          <PlaylistOverviewHeaderCenter>
            {trackURL && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "auto",
                  marginTop: theme.margins.medium,
                  gap: theme.margins.medium,
                }}
              >
                {trackSelected && (
                  <BackwardIcon
                    style={{
                      color: theme.colors.yellow,
                      fontSize: 32,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      handleOnSelectSingleRow(
                        tracks[tracks.indexOf(trackSelected) - 1]
                      )
                    }
                  />
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: theme.margins.small,
                  }}
                >
                  <AudioWaveform
                    startPlaying={isPlaying}
                    ref={waveformRef}
                    trackURL={trackURL ?? ""}
                    peaks={
                      Array.isArray(trackSelected?.trackPeaks)
                        ? trackSelected?.trackPeaks
                        : (trackSelected?.trackPeaks?.data ?? [])
                    }
                  />
                  <TrackInformation
                    trackName={trackSelected?.title ?? ""}
                    artist={trackSelected?.artist ?? ""}
                    duration={
                      useFormattingSeconds(trackSelected?.duration ?? 0)
                        .formatted
                    }
                    currentTime={useFormattingSeconds(currentTime).formatted}
                  />
                </div>
                {trackSelected && (
                  <ForwardIcon
                    style={{
                      color: theme.colors.yellow,
                      fontSize: 32,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      handleOnSelectSingleRow(
                        tracks[tracks.indexOf(trackSelected) + 1]
                      )
                    }
                  />
                )}
              </div>
            )}
          </PlaylistOverviewHeaderCenter>
          <PlaylistOverviewHeaderRight>
            <PlaylistOverviewHeaderRightContainer>
              <ButtonSection>
                <SearchInputWrapper>
                  {fetchingTracks && <Spinner small />}
                  <ErrorLabel>{errors.searchText?.message}</ErrorLabel>
                  <Controller
                    name="searchText"
                    control={control}
                    render={({ field }) => (
                      <SearchInput
                        {...field}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          debouncedDispatch(e.target.value || "");
                        }}
                        value={field.value}
                        placeholder="Search by name"
                      />
                    )}
                  />
                </SearchInputWrapper>
                <BottomButtonsContainer>
                  <TotalLabelWrapper>
                    <TotalLabel>Space Used: {0}</TotalLabel>
                  </TotalLabelWrapper>
                  <TotalLabelWrapper>
                    <TotalLabel>Total: {totalCount}</TotalLabel>
                  </TotalLabelWrapper>
                </BottomButtonsContainer>
              </ButtonSection>
            </PlaylistOverviewHeaderRightContainer>
          </PlaylistOverviewHeaderRight>
        </PlaylistOverviewHeader>
      </PageTopSection>
      <ContentContainer>
        <ContentDrawer
          isOpen={isFilterDrawerOpen}
          toggleDrawer={() => setIsFilterDrawerOpen((prev) => !prev)}
        >
          <FilterTracksForm
            fetchingTracks={fetchingTracks}
            role={roleData?.type}
            closeButtonHandler={() => setIsFilterDrawerOpen(false)}
            submitButtonHandler={async () => {
              return;
            }}
          />
        </ContentDrawer>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          <LibraryList
            handlePlayButtonClick={handlePlayButtonClick}
            tableEndRef={tableEndRef}
            data={tracks ?? []}
            loadingNextPage={isFetchingNextPage}
          />
          <RenderContentStatus
            loading={fetchingTracks}
            results={tracks?.length}
            noResultsFoundMessage="No tracks found"
          />
        </div>
      </ContentContainer>
    </WrapperContainer>
  );
};

export default Tracks;
